import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { XCircle } from 'lucide-react';
import { ME_QUERY_KEY, tokens, tsr } from '../lib/api.js';
import { clearAppVerifier, peekAppVerifier } from '../lib/nativeSso.js';
import { AuthBrand } from './auth/AuthBrand.js';

/**
 * Only ever return to a path on THIS origin — same reasoning as Login.tsx's `safeReturnPath`.
 * `next` arrives via a query param on a cross-origin redirect, which is exactly the kind of value
 * an open-redirect check exists for.
 */
function safeNextPath(value: string | null): string {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/';
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return "Can't reach the server. Check your connection and try again.";
  }
  const body = (error as { body?: unknown } | undefined)?.body;
  if (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string') {
    return body.message;
  }
  return 'Something went wrong. Please try again.';
}

/**
 * Lands here from a cross-subdomain redirect right after Login.tsx signs someone in from the
 * bare app domain (or a different tenant's subdomain) — see auth.contract.ts's `sessionHandoff`
 * doc comment for why a plain redirect can't carry a session across an origin change at all.
 *
 * The code in `?code=` is exchanged for a token pair ONCE: it's already single-use server-side,
 * and the `attempted` ref guards against React StrictMode's double-invoke finding it already gone
 * and reporting a false failure.
 *
 * In the phone app the code comes back from a sign-in that ran in the system browser
 * (`peopleorbit://sso`, lib/nativeLinks.ts) and is bound to the app: the verifier kept when that
 * sign-in started is sent with it, and forgotten afterwards whether or not the redeem worked (the
 * code is spent either way). Without a stored verifier nothing changes — the plain web hop.
 */
export function SessionHandoff() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const code = searchParams.get('code');
  const next = safeNextPath(searchParams.get('next'));

  const [status, setStatus] = useState<'redeeming' | 'error'>('redeeming');
  const [error, setError] = useState<string | null>(null);

  const redeem = tsr.auth.redeemSessionHandoff.useMutation();
  const attempted = useRef(false);

  useEffect(() => {
    if (!code) {
      setStatus('error');
      setError('This sign-in link is missing its code.');
      return;
    }
    if (attempted.current) return;
    attempted.current = true;

    const appVerifier = peekAppVerifier();
    redeem.mutate(
      { body: { code, ...(appVerifier ? { appVerifier } : {}) } },
      {
        onSuccess: async ({ body }) => {
          clearAppVerifier();
          await tokens.save(body.tokens);
          await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
          navigate(next, { replace: true });
        },
        onError: (mutationError) => {
          clearAppVerifier();
          setStatus('error');
          setError(errorMessage(mutationError));
        },
      },
    );
    // Deliberately excludes `redeem`/`queryClient`/`next`: the mutation object's identity changes
    // every render, and re-running this on that change would fight the `attempted` guard above.
  }, [code, navigate]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-bg-subtle px-4 py-10">
      <AuthBrand />

      <div className="w-full max-w-md rounded-xl border border-border bg-surface p-6 text-center shadow-card sm:p-8">
        {status === 'redeeming' ? (
          <>
            <span
              className="mx-auto mb-4 flex size-8 animate-spin rounded-full border-2 border-accent border-t-transparent"
              aria-hidden="true"
            />
            <h1 className="font-display text-lg font-bold text-fg">Signing you in…</h1>
          </>
        ) : (
          <>
            <span className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-danger-subtle text-danger">
              <XCircle className="size-6" />
            </span>
            <h1 className="font-display text-lg font-bold text-fg">Sign-in link expired</h1>
            <p className="mt-2 text-sm text-fg-muted">{error}</p>
            <Link
              to="/login"
              className="mt-6 inline-block text-sm font-medium text-accent hover:underline"
            >
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
